import { useState } from "react";
import { toast } from "sonner";
import { useTasks } from "@/hooks/useTasks";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  CheckSquare, 
  Clock, 
  AlertTriangle, 
  User, 
  Calendar,
  Filter,
  Plus,
  CheckCircle,
  XCircle,
  MoreHorizontal
} from "lucide-react";

export default function TasksPage() {
  const [selectedFilter, setSelectedFilter] = useState("all");
  const { tasks, loading, createTask, deleteTask, refetch } = useTasks();

  const handleCreateTask = async () => {
    try {
      await createTask({
        title: "Nieuwe taak",
        type: "evidence",
        description: "Aangemaakt via UI",
      });
      toast.success("Taak aangemaakt");
    } catch (e: any) {
      toast.error(e?.message || "Aanmaken mislukt");
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    try {
      await deleteTask(taskId);
      toast.success('Task deleted');
    } catch (e: any) {
      console.error('Delete task error:', e);
      toast.error(e?.message || 'Delete failed');
    }
  };

  const taskStats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'open').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    completed: tasks.filter(t => t.status === 'done').length,
    overdue: 0, // Would need due_at comparison
    critical: 0  // Would need priority field
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "done": return "default";
      case "in_progress": return "secondary";
      case "open": return "outline";
      case "blocked": return "destructive";
      default: return "outline";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "done": return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "in_progress": return <Clock className="h-4 w-4 text-blue-500" />;
      case "blocked": return <XCircle className="h-4 w-4 text-red-500" />;
      case "open": return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (selectedFilter === "all") return true;
    return task.status === selectedFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Taken laden...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Task Orchestration</h1>
          <p className="text-muted-foreground">
            AI-powered task management and workflow automation
          </p>
        </div>
        <Button onClick={handleCreateTask}>
          <Plus className="h-4 w-4 mr-2" />
          Create Task
        </Button>
      </div>

      {/* Task Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Tasks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{taskStats.total}</div>
            <p className="text-xs text-muted-foreground">All active tasks</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{taskStats.inProgress}</div>
            <p className="text-xs text-muted-foreground">Being worked on</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">  
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-600">{taskStats.pending}</div>
            <p className="text-xs text-muted-foreground">Awaiting action</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Overdue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{taskStats.overdue}</div>
            <p className="text-xs text-muted-foreground">Need attention</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Critical</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{taskStats.critical}</div>
            <p className="text-xs text-muted-foreground">High priority</p>
          </CardContent>
        </Card>
      </div>

      {/* Tasks List with Filtering */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Active Tasks</CardTitle>
              <CardDescription>
                Compliance tasks, remediation items, and evidence collection
              </CardDescription>
            </div>
            <Tabs value={selectedFilter} onValueChange={setSelectedFilter}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="open">Open</TabsTrigger>
                <TabsTrigger value="in_progress">In Progress</TabsTrigger>
                <TabsTrigger value="blocked">Blocked</TabsTrigger>
                <TabsTrigger value="done">Done</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredTasks.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Geen taken gevonden</p>
                <Button className="mt-4" onClick={handleCreateTask}>
                  <Plus className="h-4 w-4 mr-2" />
                  Eerste Task Aanmaken
                </Button>
              </div>
            ) : (
              filteredTasks.map((task) => (
                <div key={task.id} className="border rounded-lg p-4 hover:bg-muted/50">
                  <div className="flex items-start justify-between">
                    <div className="space-y-3 flex-1">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(task.status)}
                        <div>
                          <h4 className="font-medium">{task.title}</h4>
                          {task.description && (
                            <p className="text-sm text-muted-foreground">{task.description}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Badge variant={getStatusColor(task.status)} className="capitalize">
                            {task.status.replace('_', ' ')}
                          </Badge>
                          <Badge variant="outline" className="capitalize">
                            {task.type}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center gap-6 text-sm text-muted-foreground">
                        {task.assignee && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {task.assignee}
                          </span>
                        )}
                        {task.due_at && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Due {new Date(task.due_at).toLocaleDateString('nl-NL')}
                          </span>
                        )}
                        <span>Aangemaakt: {new Date(task.created_at).toLocaleDateString('nl-NL')}</span>
                        {task.source && Object.keys(task.source).length > 0 && (
                          <span>
                            {task.source.finding_id && 'Van Security Finding'}
                            {task.source.severity && ` • ${task.source.severity}`}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDeleteTask(task.id)}
                      >
                        <XCircle className="h-3 w-3 mr-1" />
                        Verwijderen
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Task Creation Automation */}
      <Card>
        <CardHeader>
          <CardTitle>Automated Task Creation</CardTitle>
          <CardDescription>AI-powered task generation from findings and controls</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-red-500" />
                <h4 className="font-medium">From Security Findings</h4>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Automatically create remediation tasks when critical or high-severity findings are detected.
              </p>
              <Badge variant="outline">23 tasks created this month</Badge>
            </div>

            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckSquare className="h-4 w-4 text-blue-500" />
                <h4 className="font-medium">From Control Gaps</h4>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Generate evidence collection tasks when compliance controls lack sufficient documentation.
              </p>
              <Badge variant="outline">12 tasks created this month</Badge>
            </div>

            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-purple-500" />
                <h4 className="font-medium">From Schedules</h4>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Create recurring tasks for policy reviews, audits, and compliance assessments.
              </p>
              <Badge variant="outline">8 tasks created this month</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}